import React, { useState, useEffect, useRef } from "react"
import * as P from "../program-state"
import * as C from "../controls"
import { preview_item_t, preview_response_t } from "../resource/types"
import { fetch_campaign_preview } from "../services/preview_service"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFacebook, faLinkedin, faTiktok } from '@fortawesome/free-brands-svg-icons'
import { faExclamationTriangle, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'

interface enhanced_preview_props {
    networks: string[]
    placements: Record<string, string[]>
    headline: string
    message: string
    link: string
    image_url?: string
    facebook_image_hash?: string
    facebook_page_id?: string
    facebook_ad_account_id?: string
    instagram_actor_id?: string
    linkedin_organization_id?: string
    linkedin_ad_account_id?: string
    tiktok_advertiser_id?: string
    tiktok_image_hash?: string
    tiktok_video_id?: string
    x_ad_account_id?: string
    facebook_cta?: string
    linkedin_cta?: string
    tiktok_cta?: string
    x_cta?: string
    children?: (parts: { iframe: React.ReactNode, controls: React.ReactNode }) => React.ReactNode
}

/**
 * Whether facebook is telling us it cannot preview for this page, as opposed to
 * the preview genuinely being unavailable.
 *
 * Substring matching is unpleasant but facebook gives no error code here. It is
 * at least in one place now: this used to be two separate lists that had already
 * drifted apart.
 */
const FACEBOOK_CANNOT_PREVIEW_SIGNS = ["system page", "test page", "Page ID"]

const facebook_cannot_preview = (errors?: string[]): boolean =>
    (errors ?? []).some(err =>
        typeof err === "string" &&
        (FACEBOOK_CANNOT_PREVIEW_SIGNS.some(sign => err.includes(sign)) ||
         (err.includes("preview") && err.includes("not available"))))

export const Enhanced_campaign_preview = ({
    networks,
    placements,
    headline,
    message,
    link,
    image_url,
    facebook_image_hash,
    facebook_page_id,
    facebook_ad_account_id,
    instagram_actor_id,
    linkedin_organization_id,
    linkedin_ad_account_id,
    tiktok_advertiser_id,
    tiktok_image_hash,
    tiktok_video_id,
    x_ad_account_id,
    facebook_cta,
    linkedin_cta,
    tiktok_cta,
    x_cta,
    children
}: enhanced_preview_props) => {
    const [_, cs] = P.use_authenticated_program_state()
    const [preview_data, set_preview_data] = useState<preview_response_t | null>(null)
    const [loading, set_loading] = useState(false)
    const [error, set_error] = useState<string | null>(null)
    const [selected_preview_index, set_selected_preview_index] = useState<number>(0)
    const [selected_network_filter, set_selected_network_filter] = useState<string | null>(null)

    // debouncing timer
    const debounce_timer = useRef<NodeJS.Timeout | null>(null)

    const fetch_preview = async () => {
        // only fetch if we have required data
        if (!headline || !message || !link || networks.length === 0) {
            return
        }

        const lowercase_networks = networks.map(n => n.toLowerCase())
        if (lowercase_networks.includes('tiktok') && !tiktok_video_id) {
            set_loading(false)
            return
        }

        set_loading(true)
        set_error(null)

        try {
            // ensure url has protocol
            let formatted_link = link
            if (link && !link.match(/^https?:\/\//)) {
                formatted_link = `https://${link}`
            }

            // ensure lowercase network names and map "meta" to "facebook"
            const lowercase_networks = networks.map(n => {
                const lower = n.toLowerCase()
                return lower === 'meta' ? 'facebook' : lower
            })

            // filter placements to only include selected networks
            const filtered_placements: Record<string, string[]> = {}
            for (const [network, placement_list] of Object.entries(placements)) {
                const lowercase_network = network.toLowerCase()
                const api_network = lowercase_network === 'meta' ? 'facebook' : lowercase_network

                // only include this placement if the network is selected AND has placements
                if (Array.isArray(placement_list) && placement_list.length > 0 && lowercase_networks.includes(api_network)) {
                    const converted_placements = api_network === 'tiktok'
                        ? placement_list.map(p => {
                            const lowercased = p.toLowerCase()
                            return lowercased === 'tiktok_pangle' ? 'pangle_native' : lowercased
                        })
                        : placement_list

                    filtered_placements[api_network] = converted_placements
                }
            }

            const creative_data: any = {
                headline,
                message
            }

            if (!lowercase_networks.includes('tiktok') || lowercase_networks.length > 1) {
                creative_data.link = formatted_link
            }

            // use image_hash for facebook if available
            if (lowercase_networks.includes('facebook') && facebook_image_hash) {
                creative_data.image_hash = facebook_image_hash
            } else if (lowercase_networks.includes('tiktok') && tiktok_video_id) {
                // TikTok requires video_id (from uploaded video), not image_hash
                creative_data.video_id = tiktok_video_id
            } else if (image_url) {
                // convert relative image url to absolute url for other networks
                let absolute_image_url = image_url
                if (!image_url.match(/^https?:\/\//)) {
                    // get the base url from the current window location
                    const base_url = `${window.location.protocol}//${window.location.host}`
                    absolute_image_url = image_url.startsWith('/')
                        ? `${base_url}${image_url}`
                        : `${base_url}/${image_url}`
                }
                creative_data.image_url = absolute_image_url
            }

            if (lowercase_networks.includes('facebook') && facebook_page_id) {
                creative_data.facebook_page_id = facebook_page_id
            }
            if (lowercase_networks.includes('facebook') && instagram_actor_id) {
                creative_data.instagram_actor_id = instagram_actor_id
            }
            if (lowercase_networks.includes('linkedin') && linkedin_organization_id) {
                creative_data.linkedin_organization_id = linkedin_organization_id
            }

            if (lowercase_networks.includes('facebook') && facebook_cta) {
                creative_data.call_to_action = facebook_cta
            } else if (lowercase_networks.includes('linkedin') && linkedin_cta) {
                creative_data.call_to_action = linkedin_cta
            } else if (lowercase_networks.includes('tiktok') && tiktok_cta) {
                creative_data.call_to_action = tiktok_cta
            } else if (lowercase_networks.includes('x') && x_cta) {
                creative_data.call_to_action = x_cta
            }

            // build ad_account_ids object for selected networks
            const ad_account_ids: Record<string, string> = {}
            if (lowercase_networks.includes('facebook') && facebook_ad_account_id) {
                ad_account_ids.facebook = facebook_ad_account_id
            }
            if (lowercase_networks.includes('linkedin') && linkedin_ad_account_id) {
                ad_account_ids.linkedin = linkedin_ad_account_id
            }
            if (lowercase_networks.includes('tiktok') && tiktok_advertiser_id) {
                ad_account_ids.tiktok = tiktok_advertiser_id
            }
            if (lowercase_networks.includes('x') && x_ad_account_id) {
                ad_account_ids.x = x_ad_account_id
            }

            const request = {
                networks: lowercase_networks,
                placements: filtered_placements,
                creative: creative_data,
                ad_account_ids: Object.keys(ad_account_ids).length > 0 ? ad_account_ids : undefined
            }

            const [ok, resp] = await fetch_campaign_preview(request, cs.token)

            if (ok) {
                // check if we got empty previews
                if (resp.previews && resp.previews.length === 0) {

                    const missing_items = []
                    if (!facebook_page_id && networks.includes('facebook')) {
                        missing_items.push("Facebook page")
                    }
                    if (!tiktok_advertiser_id && networks.includes('tiktok')) {
                        missing_items.push("TikTok advertiser account")
                    }

                    // Facebook cannot render a preview for a sandbox or system
                    // page, which is exactly the situation on the review and test
                    // environments, so fall back to a plain rendering rather than
                    // showing nothing.
                    if (facebook_cannot_preview(resp.errors)) {
                        set_error("preview_fallback")
                    } else {
                        // Anything else genuinely has no preview and saying so is
                        // more use than a mock. This used to also fall back
                        // whenever a facebook creative had no image hash, which is
                        // true of every video - videos are never uploaded to
                        // facebook - so video ads always showed the mock.
                        set_error(missing_items.length > 0
                            ? `No preview yet. Add: ${missing_items.join(", ")}.`
                            : "No preview available for this combination.")
                    }
                } else {
                    set_preview_data(resp)
                }
            } else {
                console.error("Preview fetch failed. Status:", resp?.status || resp?.statusCode)
                console.error("Error response:", resp)

                const error_message = resp?.detail || resp?.message || JSON.stringify(resp)
                if (facebook_cannot_preview([error_message])) {
                    set_error("preview_fallback")
                } else {
                    set_error("We could not generate a preview. The ad itself is unaffected.")
                }
            }
        } catch (err) {
            set_error("Error loading preview")
            console.error("Preview error:", err)
        } finally {
            set_loading(false)
        }
    }

    // debounced preview fetch
    useEffect(() => {
        if (debounce_timer.current) {
            clearTimeout(debounce_timer.current)
        }

        debounce_timer.current = setTimeout(() => {
            fetch_preview()
        }, 500) 

        return () => {
            if (debounce_timer.current) {
                clearTimeout(debounce_timer.current)
            }
        }
    }, [headline, message, link, image_url, networks, placements, facebook_page_id, facebook_image_hash, tiktok_image_hash, tiktok_video_id, linkedin_organization_id, linkedin_ad_account_id, tiktok_advertiser_id])

    const get_network_icon = (network: string) => {
        switch (network) {
            case 'facebook':
            case 'meta':
                return faFacebook
            case 'linkedin':
                return faLinkedin
            case 'tiktok':
                return faTiktok
            default:
                return faFacebook
        }
    }

    const get_network_display_name = (network: string) => {
        switch (network) {
            case 'facebook':
            case 'meta':
                return 'Facebook'
            case 'linkedin':
                return 'LinkedIn'
            case 'tiktok':
                return 'TikTok'
            default:
                return network
        }
    }

    // get unique networks from previews
    const get_available_networks = () => {
        if (!preview_data || preview_data.previews.length === 0) {
            return []
        }
        const unique_networks = Array.from(new Set(preview_data.previews.map(p => p.network)))
        return unique_networks
    }

    // filter previews by selected network
    const get_filtered_previews = () => {
        if (!preview_data || preview_data.previews.length === 0) {
            return []
        }
        if (!selected_network_filter) {
            return preview_data.previews
        }
        return preview_data.previews.filter(p => p.network === selected_network_filter)
    }

    // navigate to previous preview
    const go_to_previous = () => {
        const filtered = get_filtered_previews()
        if (filtered.length === 0) return
        set_selected_preview_index((prev) => (prev === 0 ? filtered.length - 1 : prev - 1))
    }

    // navigate to next preview
    const go_to_next = () => {
        const filtered = get_filtered_previews()
        if (filtered.length === 0) return
        set_selected_preview_index((prev) => (prev === filtered.length - 1 ? 0 : prev + 1))
    }

    // keyboard navigation
    useEffect(() => {
        const handle_keydown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                go_to_previous()
            } else if (e.key === 'ArrowRight') {
                go_to_next()
            }
        }
        window.addEventListener('keydown', handle_keydown)
        return () => window.removeEventListener('keydown', handle_keydown)
    }, [preview_data, selected_network_filter])

    // reset preview index when network filter changes
    useEffect(() => {
        set_selected_preview_index(0)
    }, [selected_network_filter])

    // set initial network filter when previews load
    useEffect(() => {
        if (preview_data && preview_data.previews.length > 0 && !selected_network_filter) {
            const first_network = preview_data.previews[0].network
            set_selected_network_filter(first_network)
        }
    }, [preview_data])

    const render_network_filter = () => {
        const available_networks = get_available_networks()
        if (available_networks.length <= 1) {
            return null
        }

        return (
            <div className="tw-flex tw-space-x-2 tw-mb-4">
                {available_networks.map((network) => (
                    <button
                        key={network}
                        onClick={() => set_selected_network_filter(network)}
                        className={`tw-flex tw-items-center tw-space-x-2 tw-px-4 tw-py-2 tw-rounded-lg tw-transition-colors tw-font-medium ${
                            selected_network_filter === network
                                ? "tw-bg-blue-500 tw-text-white"
                                : "tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-gray-200"
                        }`}
                    >
                        <FontAwesomeIcon icon={get_network_icon(network)} />
                        <span>{get_network_display_name(network)}</span>
                    </button>
                ))}
            </div>
        )
    }

    const render_preview_navigation = () => {
        const filtered_previews = get_filtered_previews()
        if (filtered_previews.length === 0) {
            return null
        }

        const current_preview = filtered_previews[selected_preview_index]
        const total_count = filtered_previews.length

        return (
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4 tw-bg-gray-50 tw-rounded-lg tw-p-4">
                <button
                    onClick={go_to_previous}
                    disabled={total_count <= 1}
                    className="tw-p-2 tw-rounded-lg tw-bg-white tw-border tw-border-gray-300 hover:tw-bg-gray-100 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                    aria-label="Previous preview"
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>

                <div className="tw-flex-1 tw-mx-4 tw-text-center">
                    <div className="tw-flex tw-items-center tw-justify-center tw-space-x-2 tw-mb-1">
                        <FontAwesomeIcon icon={get_network_icon(current_preview.network)} className="tw-text-gray-600" />
                        <span className="tw-font-semibold tw-text-gray-900">{current_preview.placement_name}</span>
                        {current_preview.device_type && (
                            <span className="tw-px-2 tw-py-1 tw-bg-gray-200 tw-text-gray-700 tw-text-xs tw-rounded-full tw-font-medium">
                                {current_preview.device_type}
                            </span>
                        )}
                    </div>
                    <div className="tw-text-sm tw-text-gray-600">
                        Preview {selected_preview_index + 1} of {total_count}
                    </div>
                </div>

                <button
                    onClick={go_to_next}
                    disabled={total_count <= 1}
                    className="tw-p-2 tw-rounded-lg tw-bg-white tw-border tw-border-gray-300 hover:tw-bg-gray-100 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                    aria-label="Next preview"
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
        )
    }

    const render_iframe_preview = (preview: preview_item_t) => {
        if (!preview.preview_data.src) {
            return <div>No preview URL available</div>
        }

        const width = preview.preview_data.width || "500"
        const height = preview.preview_data.height || "600"

        return (
            <div className="tw-w-full tw-flex tw-justify-center tw-items-start tw-overflow-auto tw-py-4">
                <div className="tw-flex tw-justify-center tw-items-center" style={{ minWidth: `${width}px` }}>
                    <iframe
                        src={preview.preview_data.src}
                        width={width}
                        height={height}
                        frameBorder="0"
                        className="tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm tw-bg-white"
                    />
                </div>
            </div>
        )
    }

    const render_component_preview = (preview: preview_item_t) => {
        // for tiktok component-based previews
        const data = preview.preview_data.component_data
        
        return (
            <div className="tw-max-w-sm tw-mx-auto tw-bg-black tw-rounded-lg tw-overflow-hidden">
                <div className="tw-relative">
                    {image_url && (
                        <img 
                            src={image_url} 
                            alt="Ad preview" 
                            className="tw-w-full tw-h-auto"
                        />
                    )}
                    <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-bg-gradient-to-t tw-from-black tw-to-transparent tw-p-4">
                        <div className="tw-text-white">
                            <h3 className="tw-font-bold tw-text-lg tw-mb-1">{headline}</h3>
                            <p className="tw-text-sm tw-opacity-90">{message}</p>
                            <button className="tw-mt-2 tw-bg-red-600 tw-text-white tw-px-4 tw-py-2 tw-rounded tw-text-sm tw-font-semibold">
                                Learn More
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const render_preview_content = () => {
        if (loading) {
            return (
                <div className="tw-flex tw-justify-center tw-items-center tw-h-96">
                    <C.Eres_spin_placeholder />
                </div>
            )
        }

        if (error) {
            // show static fallback preview for system/test pages
            if (error === "preview_fallback") {
                return (
                    <div className="tw-p-6">
                        <Static_preview_component
                            headline={headline}
                            message={message}
                            link={link}
                            image_url={image_url}
                        />
                    </div>
                )
            }

            return (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-96 tw-text-red-600">
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="tw-mb-2" />
                    <p>{error}</p>
                </div>
            )
        }

        const filtered_previews = get_filtered_previews()

        if (filtered_previews.length === 0) {
            return (
                <div className="tw-flex tw-justify-center tw-items-center tw-h-96 tw-text-gray-500">
                    <p>No preview available. Please fill in all required fields.</p>
                </div>
            )
        }

        const current_preview = filtered_previews[selected_preview_index]

        if (current_preview.error) {
            return (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-96 tw-text-red-600">
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="tw-mb-2" />
                    <p>{current_preview.error}</p>
                </div>
            )
        }

        switch (current_preview.preview_type) {
            case 'iframe':
                return render_iframe_preview(current_preview)
            case 'component':
                return render_component_preview(current_preview)
            case 'image':
                return (
                    <div className="tw-flex tw-justify-center tw-items-center tw-py-4">
                        <img
                            src={current_preview.preview_data.src}
                            alt="Ad preview"
                            className="tw-max-w-full tw-h-auto tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm"
                        />
                    </div>
                )
            case 'html':
                return (
                    <div
                        className="tw-p-4 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg"
                        dangerouslySetInnerHTML={{ __html: current_preview.preview_data.html_content || '' }}
                    />
                )
            default:
                return <div>Unknown preview type</div>
        }
    }

    const preview_iframe = (
        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-p-6">
            <h3 className="tw-text-lg tw-font-semibold tw-mb-4">Ad Preview</h3>
            {render_network_filter()}
            {render_preview_navigation()}
            <div className="tw-min-h-[500px] tw-max-h-[800px] tw-overflow-auto tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-50">
                {render_preview_content()}

                {/* Placements the preview service could not render. This was
                    parsed and then thrown away, so ticking one of them showed
                    nothing and said nothing about why. */}
                {(preview_data?.unsupported_placements?.length ?? 0) > 0 && (
                    <div className="tw-mt-3 tw-rounded tw-border tw-border-amber-300 tw-bg-amber-50 tw-p-3 tw-text-sm tw-text-amber-900">
                        <p className="tw-font-medium tw-mb-1">
                            No preview for {preview_data!.unsupported_placements.length} placement
                            {preview_data!.unsupported_placements.length === 1 ? "" : "s"}
                        </p>
                        <ul className="tw-list-disc tw-ms-5">
                            {preview_data!.unsupported_placements.map(up => (
                                <li key={`${up.network}-${up.placement}`}>
                                    {up.placement.replace(/^[a-z_]+__/, "").replace(/_/g, " ")} — {up.reason}
                                </li>
                            ))}
                        </ul>
                        <p className="tw-mt-1">The ad will still run there.</p>
                    </div>
                )}
            </div>
        </div>
    )

    const preview_controls = (
        <div>
        </div>
    )

    if (children) {
        return <>{children({ iframe: preview_iframe, controls: preview_controls })}</>
    }

    return (
        <>
            {preview_iframe}
            {preview_controls}
        </>
    )
}

// fallback component for when api is not available
export const Static_preview_component = ({
    headline,
    message,
    link,
    image_url
}: {
    headline: string
    message: string
    link: string
    image_url?: string
}) => {
    return (
        <div className="tw-max-w-md tw-mx-auto">
            {image_url && (
                <img
                    src={image_url}
                    alt="Ad preview"
                    className="tw-w-full tw-h-auto tw-rounded-lg tw-mb-4"
                />
            )}
            <h4 className="tw-font-semibold tw-text-lg tw-mb-2">{headline || "Headline"}</h4>
            <p className="tw-text-gray-700 tw-mb-3">{message || "Ad message"}</p>
            <a
                href={link || "#"}
                className="tw-text-blue-600 hover:tw-underline"
                target="_blank"
                rel="noopener noreferrer"
            >
                {link || "www.example.com"}
            </a>
        </div>
    )
}